import { Link } from 'react-router-dom';
import LogisticsClusterPage from './LogisticsClusterPage';
import { Helmet } from 'react-helmet-async';

const EcommerceSummaryBlock = () => {
  const lastUpdated = new Date().toISOString().split('T')[0];
  return (
    <section 
      style={{ padding: '40px 24px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}
      aria-label="Ecommerce Fulfilment Summary"
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto', background: '#ffffff', padding: '32px', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', borderLeft: '5px solid #28479e' }}>
        <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '12px', fontWeight: 600 }}>
          Last Updated: {lastUpdated}
        </div>
        <h2 style={{ fontSize: '1.75rem', color: '#0f172a', marginBottom: '16px', fontWeight: 800 }}>
          Quick Summary: Ecommerce Fulfilment Services
        </h2>
        <p style={{ fontSize: '1.05rem', color: '#475569', lineHeight: 1.7, marginBottom: '16px' }}>
          <strong>Top Summary & Definition:</strong> Ecommerce fulfilment services encompass the entire process of receiving, storing, picking, packing, and dispatching online orders to consumers. Pick Pack Pro provides these services from our central UK hub in Milton Keynes, directly integrating with Shopify, TikTok Shop, and Amazon FBA.
        </p>
        <p style={{ fontSize: '1.05rem', color: '#475569', lineHeight: 1.7, marginBottom: '0' }}>
          <strong>Key Takeaway:</strong> By outsourcing to a 3PL, growing brands can guarantee 99.9% picking accuracy and next-day UK delivery SLAs, freeing up time to focus entirely on marketing and sales rather than warehouse operations.
        </p>
      </div>
    </section>
  );
};

const cardStyle = {
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  padding: '32px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  display: 'flex',
  flexDirection: 'column' as const,
  height: '100%'
};

const titleStyle = {
  fontSize: '1.4rem',
  color: '#2d3748',
  fontWeight: '700',
  marginBottom: '1rem',
  borderBottom: '2px solid #edf2f7',
  paddingBottom: '0.75rem',
  lineHeight: '1.4'
};

const textStyle = {
  fontSize: '1rem',
  lineHeight: '1.7',
  color: '#4a5568',
  margin: 0,
  flexGrow: 1
};

const partners = [
  { name: "Shopify Fulfillment Solutions", url: "https://www.shopify.com/uk/fulfillment", desc: "Our systems integrate directly." },
  { name: "CIPS Logistics Standards", url: "https://www.cips.org/knowledge/procurement-topics-and-skills/logistics/", desc: "Ensuring we remain the best uk fulfilment company." },
  { name: "UK Government Business & Trade", url: "https://www.gov.uk/business-and-industry/trade-and-investment", desc: "Supporting e-commerce fulfillment im vereinigten kA nigreich." },
  { name: "Amazon Seller Central", url: "https://sellercentral.amazon.co.uk/", desc: "For fulfillment ecommerce compliance." },
  { name: "Linnworks Inventory Management", url: "https://www.linnworks.com/", desc: "Powering our cloud fulfilment." },
  { name: "Mintsoft WMS", url: "https://www.mintsoft.com/", desc: "Driving our e commerce logistics uk." },
  { name: "TikTok Shop UK", url: "https://sell.tiktok.com/uk/", desc: "Empowering viral packpro ecommerce sales." },
  { name: "DPD Courier Services", url: "https://www.dpd.co.uk/", desc: "Premium ecommerce delivery services." },
  { name: "Which? Consumer Rights", url: "https://www.which.co.uk/consumer-rights/regulation/consumer-contracts-regulations-ajWHC8m21cAk", desc: "Ensuring our order fulfilment service uk protects buyers." },
  { name: "BIFA", url: "https://www.bifa.org/", desc: "Vetting ecommerce logistics companies." }
];

const SeoKeywordBlock = () => (
  <section className="logistics-page-section logistics-page-section--white" aria-labelledby="ecommerce-fulfilment-seo">
    <div className="logistics-page-section-inner" style={{ maxWidth: '1200px', margin: '0 auto' }}>
      
      <header className="logistics-page-section-header" style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <h2 id="ecommerce-fulfilment-seo" className="logistics-page-section-title" style={{ fontSize: '2.5rem', fontWeight: '800', color: '#1a202c', marginBottom: '1rem' }}>
          Premier Ecommerce Fulfilment Services in the UK
        </h2>
        <p className="logistics-page-section-lead" style={{ fontSize: '1.1rem', color: '#4a5568', maxWidth: '800px', margin: '0 auto' }}>
          Scaling your online store requires the most reliable <strong>ecommerce fulfillment</strong> operations. Pick Pack Pro provides professional <strong>fulfilment services</strong> designed to supercharge your brand and deliver exceptional customer experiences.
        </p>
      </header>

      <div style={{ padding: '0 1rem' }}>
        <div style={{ background: '#f8fafc', padding: '2rem', borderRadius: '12px', marginBottom: '3rem', borderLeft: '4px solid #28479e' }}>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: '#334155', margin: 0 }}>
            Whether you spell it <strong>ecommerce fulfilment</strong> or <strong>ecommerce fulfillment</strong>, a robust logistics pipeline is the backbone of modern digital retail. Partnering with a top-tier provider ensures your brand offers seamless delivery and exceptional <strong>fulfilment services</strong> from checkout to the customer's doorstep.
          </p>
        </div>

        {/* Feature Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2rem', marginBottom: '4rem' }}>
          
          <div style={cardStyle}>
            <h2 style={titleStyle}>Why Brands Need Dedicated Ecommerce Fulfillment Services</h2>
            <p style={textStyle}>
              By outsourcing to dedicated <strong>ecommerce fulfilment services</strong>, brands can stop worrying about packing boxes and start focusing on growth. From multi-channel inventory syncing to same-day dispatch, our <strong>ecommerce fulfillment services</strong> handle the complexities of Amazon, Shopify, and TikTok Shop flawlessly.
            </p>
          </div>

          <div style={cardStyle}>
            <h2 style={titleStyle}>Choosing the Right Fulfilment Company</h2>
            <p style={textStyle}>
              Not every <strong>fulfilment company</strong> is built the same. When evaluating the best <strong>fulfilment companies uk</strong>, you need a partner that offers transparent pricing, rapid SLA compliance, and advanced WMS tech. Pick Pack Pro stands out by operating our own facility rather than acting as a broker.
            </p>
          </div>

          <div style={cardStyle}>
            <h2 style={titleStyle}>Inside Our Leading Fulfilment Centre UK</h2>
            <p style={textStyle}>
              Strategically located in Milton Keynes, our <strong>fulfilment centre uk</strong> acts as a super-hub for the entire country. This central location powers our core <strong>ecommerce fulfilment uk</strong> operations, allowing us to hit next-day delivery cut-offs efficiently.
            </p>
          </div>

          <div style={cardStyle}>
            <h2 style={titleStyle}>A Comprehensive UK Fulfilment Service You Can Trust</h2>
            <p style={textStyle}>
              As a fully integrated <strong>uk fulfilment service</strong>, we go beyond simple pick and pack. We offer bespoke contract packing, return handling, and strict Amazon FBA prep services. Whether you need standard B2C dropshipping or complex B2B pallet builds, our systems adapt to your SKUs.
            </p>
          </div>

        </div>

        {/* Industry Partners Section */}
        <div style={{ marginBottom: '4rem' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1a202c', textAlign: 'center', marginBottom: '2rem' }}>
            Industry Authorities & Partners
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {partners.map((p, idx) => (
              <a key={idx} href={p.url} target="_blank" rel="noopener noreferrer nofollow" style={{
                display: 'block', padding: '1.5rem', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', textDecoration: 'none', transition: 'transform 0.2s, boxShadow 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }} onMouseOver={(e) => (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)')} onMouseOut={(e) => (e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)')}>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#28479e', fontSize: '1.1rem', fontWeight: '600' }}>{p.name}</h3>
                <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem', lineHeight: '1.5' }}>{p.desc}</p>
              </a>
            ))}
          </div>
        </div>

        {/* Internal Resources */}
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '2rem', textAlign: 'center', marginBottom: '4rem' }}>
          <h3 style={{ fontSize: '1.25rem', color: '#166534', marginBottom: '1rem' }}>Explore Our Core Resources</h3>
          <p style={{ color: '#15803d', lineHeight: '1.8' }}>
            Discover our <Link to="/warehousing-services" style={{ fontWeight: 'bold', color: '#166534', textDecoration: 'underline' }}>warehousing services</Link>, learn about <Link to="/fulfillment-by-merchant-services" style={{ fontWeight: 'bold', color: '#166534', textDecoration: 'underline' }}>FBM fulfilment</Link>, read our guide on <Link to="/blog/pick-and-pack-fulfilment-uk-explained" style={{ fontWeight: 'bold', color: '#166534', textDecoration: 'underline' }}>pick and pack fulfilment explained</Link>, tour our <Link to="/our-fulfilment-center" style={{ fontWeight: 'bold', color: '#166534', textDecoration: 'underline' }}>Milton Keynes fulfilment centre</Link>, view all <Link to="/logistics-services" style={{ fontWeight: 'bold', color: '#166534', textDecoration: 'underline' }}>logistics services</Link>, check <Link to="/pricing-fulfilment-services" style={{ fontWeight: 'bold', color: '#166534', textDecoration: 'underline' }}>ecommerce fulfillment uk pricing</Link>, and <Link to="/contact-fulfillment-services" style={{ fontWeight: 'bold', color: '#166534', textDecoration: 'underline' }}>contact us</Link> today.
          </p>
        </div>

      </div>
    </div>
  </section>
);

export default function EcommerceFulfillmentServices() {
  return (
    <>
      <Helmet>
        <script type="application/ld+json" nonce="ppp-2026-gtm-nonce">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Ecommerce Fulfilment Services UK",
            "description": "Looking for reliable ecommerce fulfilment companies UK? We provide ecommerce fulfillment services UK with live inventory tracking and seamless integrations.",
            "datePublished": "2026-08-04T00:00:00+00:00",
            "dateModified": "2026-08-04T00:00:00+00:00",
            "author": {
              "@type": "Organization",
              "name": "Pick Pack Pro",
              "url": "https://www.pickpackpro.co.uk/"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Pick Pack Pro",
              "url": "https://www.pickpackpro.co.uk/",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.pickpackpro.co.uk/Images/ppp-orange-Blue-logo.webp"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://www.pickpackpro.co.uk/ecommerce-fulfillment-services"
            }
          })}
        </script>
      </Helmet>
      <LogisticsClusterPage clusterId="ecommerce" customSEOBlock={<SeoKeywordBlock />} summaryBlock={<EcommerceSummaryBlock />} />
    </>
  );
}
